import { Box, Grid, Typography } from "@mui/material";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect } from "react";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { TemplateContentType } from "features/Influencer/ContactList/schema";
import { trackEvent } from "utils/tracking";

import styles from "./styles";

export type Step1Data = {
  name: string;
  type: TemplateContentType | null;
};

type TypeOption = {
  id: TemplateContentType;
  label: string;
  sub: string;
  icon: string;
  background: string;
  textColor: string;
  subColor: string;
  iconBg: string;
  iconColor: string;
  border?: string;
  checkColor: string;
};

const TYPE_OPTIONS: TypeOption[] = [
  {
    id: TemplateContentType.OUTREACH,
    label: "Outreach",
    sub: "First email to a brand",
    icon: "fa-solid fa-paper-plane",
    background:
      "linear-gradient(57.59deg, #151F16 -1.58%, #004537 98.35%), linear-gradient(180deg, rgba(236, 234, 229, 0) 0%, #ECEAE5 100%), #FFFFFF",
    textColor: "#FFFFFF",
    subColor: "rgba(255, 255, 255, 0.75)",
    iconBg: "rgba(255, 255, 255, 0.12)",
    iconColor: "#FFFFFF",
    checkColor: "#FFFFFF",
  },
  {
    id: TemplateContentType.FOLLOWUP,
    label: "Follow-up",
    sub: "Nudge after no reply",
    icon: "fa-solid fa-reply",
    background: "#FFFFFF",
    textColor: "#151F16",
    subColor: "rgba(21, 31, 22, 0.6)",
    iconBg: "#F0F2EE",
    iconColor: "#004537",
    border: "1px solid rgba(21, 31, 22, 0.08)",
    checkColor: "#004537",
  },
];

export function Step1Basics({
  value,
  onChange,
  onContinue,
}: {
  value: Step1Data;
  onChange: (v: Step1Data) => void;
  onContinue: () => void;
}) {
  const { templates } = useContext(OutreachTemplatesContext);

  const isUserFirstTemplate = templates?.filter((x) => x.id!)?.length === 0;

  useEffect(() => {
    trackEvent("Template - Pick Type Step Opened", {
      "Is First Template": isUserFirstTemplate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If it's the user's first template, we don't require them to select a type.
  // We will create both an outreach and followup for them.
  const canContinue = isUserFirstTemplate ? true : !!value.type;
  return (
    <Box>
      {!isUserFirstTemplate && (
        <>
          <Typography variant="h4" sx={{ mb: 3 }}>
            Pick a template type
          </Typography>

          <Grid container spacing={2} sx={styles.cardsGrid}>
            {TYPE_OPTIONS.map((option) => {
              const active = value.type === option.id;
              return (
                <Grid item xs={6} key={option.id}>
                  <Box
                    onClick={() => {
                      trackEvent("Template - Pick Type", {
                        Type: option.id,
                      });
                      onChange({ ...value, type: option.id });
                    }}
                    sx={styles.outreachCard(option, active)}
                  >
                    <Box sx={styles.iconWrapper(option)}>
                      <Box
                        component="i"
                        className={option.icon}
                        sx={styles.icon(option)}
                      />
                    </Box>

                    <Box>
                      <Typography sx={styles.cardLabel(option)}>
                        {option.label}
                      </Typography>
                      <Typography sx={styles.cardSub(option)}>
                        {option.sub}
                      </Typography>
                    </Box>

                    <Box
                      component="i"
                      className={
                        active
                          ? "fa-solid fa-circle-check"
                          : "fa-regular fa-circle"
                      }
                      sx={styles.cardCheck(option, active)}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <Box sx={styles.actionsRowEnd}>
        <StyledLargeButton
          variant="contained"
          disableElevation
          disabled={!canContinue}
          onClick={() => {
            trackEvent("Template - Pick Type Continue Pressed", {
              Type: value.type,
              "Is First Template": isUserFirstTemplate,
            });
            onContinue();
          }}
        >
          Continue
        </StyledLargeButton>
      </Box>
    </Box>
  );
}
